import { Vec2 } from '../utils/math';
import { Component, LaserSource, FlatMirror, Lens, Prism, GlassPlate, CurvedMirror, Aperture, BeamSplitter, Detector, DiffractionGrating, Medium, Polarizer, deserializeComponent } from '../engine/Component';
import { LightEngine } from '../engine/LightEngine';
import { ConstraintSolver, SnapSolution } from './ConstraintSolver';

export type ToolMode = 'select' | 'delete' | 'laser' | 'mirror' | 'lens' | 'prism' | 'glass' | 'curved_mirror' | 'aperture' | 'beam_splitter' | 'detector' | 'diffraction_grating' | 'medium' | 'polarizer';

export class CanvasController {
  private canvas: HTMLCanvasElement;
  private engine: LightEngine;
  private constraintSolver: ConstraintSolver;
  private toolMode: ToolMode = 'select';
  private isDragging = false;
  private dragComponent: Component | null = null;
  private dragOffset: Vec2 = new Vec2(0, 0);
  private mousePos: Vec2 = new Vec2(0, 0); // world-space mouse position
  private isPlacing = false;
  private currentSnap: SnapSolution | null = null;
  private wheelResetTimer: number = 0;

  // Pan state
  private isPanning = false;
  private lastPanScreenPos: Vec2 = new Vec2(0, 0);
  private spaceHeld = false;

  // Clipboard for copy/paste
  private clipboard: Record<string, unknown> | null = null;

  onComponentSelected?: (comp: Component | null) => void;
  onSceneChanged?: () => void;

  constructor(canvas: HTMLCanvasElement, engine: LightEngine) {
    this.canvas = canvas;
    this.engine = engine;
    this.constraintSolver = new ConstraintSolver();
    this.bindEvents();
  }

  setTool(tool: ToolMode): void {
    this.toolMode = tool;
    this.isPlacing = tool !== 'select' && tool !== 'delete';
    this.canvas.style.cursor = this.isPlacing ? 'crosshair' : (tool === 'delete' ? 'not-allowed' : 'default');
  }

  getTool(): ToolMode {
    return this.toolMode;
  }

  getCurrentSnap(): SnapSolution | null {
    return this.currentSnap;
  }

  getClipboard(): Record<string, unknown> | null {
    return this.clipboard;
  }

  copySelected(): void {
    const comp = this.engine.scene.get(this.engine.selectedId ?? -1);
    if (comp) {
      this.clipboard = comp.serialize();
    }
  }

  pasteAtMouse(): void {
    if (!this.clipboard) return;
    const data = { ...this.clipboard };
    delete data.id; // get a new ID
    // Offset from original position toward mouse
    const pos = data.position as { x: number; y: number };
    if (pos) {
      pos.x = this.mousePos.x;
      pos.y = this.mousePos.y;
    }
    this.engine.pushHistory();
    const comp = deserializeComponent(data);
    this.engine.scene.add(comp);
    this.engine.selectedId = comp.id;
    this.engine.markDirty();
    this.onComponentSelected?.(comp);
    this.onSceneChanged?.();
  }

  private bindEvents(): void {
    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });
    this.canvas.addEventListener('contextmenu', (e) => e.preventDefault());

    // Track space key for pan mode
    document.addEventListener('keydown', (e) => {
      if (e.code === 'Space' && !e.repeat) {
        this.spaceHeld = true;
        this.canvas.style.cursor = 'grab';
      }
    });
    document.addEventListener('keyup', (e) => {
      if (e.code === 'Space') {
        this.spaceHeld = false;
        if (!this.isPanning) {
          this.canvas.style.cursor = this.isPlacing ? 'crosshair' : 'default';
        }
      }
    });
  }

  /** Convert screen mouse event to world coordinates */
  private getWorldPos(e: MouseEvent): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    const screenPos = new Vec2(e.clientX - rect.left, e.clientY - rect.top);
    const size = this.engine.getRenderer().getCanvasSize();
    return this.engine.camera.screenToWorld(screenPos, size.width, size.height);
  }

  /** Get raw screen position (for pan) */
  private getScreenPos(e: MouseEvent): Vec2 {
    const rect = this.canvas.getBoundingClientRect();
    return new Vec2(e.clientX - rect.left, e.clientY - rect.top);
  }

  private hitTest(pos: Vec2): Component | null {
    const components = this.engine.scene.getAll();
    const padding = 10 / this.engine.camera.zoom; // scale padding with zoom
    for (let i = components.length - 1; i >= 0; i--) {
      const comp = components[i];
      const bounds = comp.getBounds();
      if (
        pos.x >= bounds.x - padding &&
        pos.x <= bounds.x + bounds.width + padding &&
        pos.y >= bounds.y - padding &&
        pos.y <= bounds.y + bounds.height + padding
      ) {
        return comp;
      }
    }
    return null;
  }

  private updateConstraintIndex(excludeSourceId?: number): void {
    this.constraintSolver.indexSegments(this.engine.tracedSegments, excludeSourceId);
  }

  private onMouseDown = (e: MouseEvent): void => {
    const screenPos = this.getScreenPos(e);

    // Middle mouse button or Space+click = pan
    if (e.button === 1 || (e.button === 0 && this.spaceHeld)) {
      this.isPanning = true;
      this.lastPanScreenPos = screenPos;
      this.canvas.style.cursor = 'grabbing';
      return;
    }

    const pos = this.getWorldPos(e);
    this.mousePos = pos;

    if (this.isPlacing) {
      this.placeComponent(pos);
      return;
    }

    if (this.toolMode === 'delete') {
      const hit = this.hitTest(pos);
      if (hit) {
        this.engine.pushHistory();
        this.engine.scene.remove(hit.id);
        this.engine.selectedId = undefined;
        this.engine.markDirty();
        this.onComponentSelected?.(null);
        this.onSceneChanged?.();
      }
      return;
    }

    // Select mode
    const hit = this.hitTest(pos);
    if (hit) {
      this.isDragging = true;
      this.dragComponent = hit;
      this.dragOffset = hit.position.sub(pos);
      this.engine.pushHistory();
      this.engine.selectedId = hit.id;
      this.onComponentSelected?.(hit);
      this.updateConstraintIndex(hit.type === 'laser' ? hit.id : undefined);
    } else {
      this.engine.selectedId = undefined;
      this.onComponentSelected?.(null);
    }
    this.engine.markDirty();
  };

  private onMouseMove = (e: MouseEvent): void => {
    const screenPos = this.getScreenPos(e);

    // Handle panning
    if (this.isPanning) {
      const dx = screenPos.x - this.lastPanScreenPos.x;
      const dy = screenPos.y - this.lastPanScreenPos.y;
      this.engine.camera.pan(dx, dy);
      this.lastPanScreenPos = screenPos;
      this.engine.markDirty();
      return;
    }

    const pos = this.getWorldPos(e);
    this.mousePos = pos;
    this.currentSnap = null;

    if (this.isDragging && this.dragComponent) {
      const freePos = pos.add(this.dragOffset);

      if (e.shiftKey) {
        const snap = this.constraintSolver.findBestSnap(freePos, this.dragComponent);
        if (snap) {
          this.currentSnap = snap;
          this.dragComponent.position = snap.position;
          this.engine.setSnapIndicator(snap.position, snap.hint);
        } else {
          this.dragComponent.position = freePos;
          this.engine.setSnapIndicator(null);
        }
      } else {
        this.dragComponent.position = this.snapToGrid(freePos);
        this.engine.setSnapIndicator(null);
      }

      this.engine.markDirty();
      this.onSceneChanged?.();
    }

    if (this.isPlacing) {
      this.engine.markDirty();
    }
  };

  private onMouseUp = (_e: MouseEvent): void => {
    if (this.isPanning) {
      this.isPanning = false;
      this.canvas.style.cursor = this.spaceHeld ? 'grab' : (this.isPlacing ? 'crosshair' : 'default');
      return;
    }

    this.isDragging = false;
    this.dragComponent = null;
    this.currentSnap = null;
    this.engine.setSnapIndicator(null);
    this.engine.markDirty();
  };

  private wheelHistoryPushed = false;

  private onWheel = (e: WheelEvent): void => {
    e.preventDefault();

    // If a component is selected, scroll = rotate it
    const comp = this.engine.scene.get(this.engine.selectedId ?? -1);
    if (comp && !e.ctrlKey) {
      if (!this.wheelHistoryPushed) {
        this.engine.pushHistory();
        this.wheelHistoryPushed = true;
        clearTimeout(this.wheelResetTimer);
        this.wheelResetTimer = window.setTimeout(() => { this.wheelHistoryPushed = false; }, 500);
      } else {
        clearTimeout(this.wheelResetTimer);
        this.wheelResetTimer = window.setTimeout(() => { this.wheelHistoryPushed = false; }, 500);
      }

      const delta = e.deltaY > 0 ? 0.05 : -0.05;
      comp.angle += delta;
      this.engine.markDirty();
      this.onSceneChanged?.();
      return;
    }

    // No component selected or Ctrl held = zoom
    const screenPos = this.getScreenPos(e);
    const size = this.engine.getRenderer().getCanvasSize();
    const factor = e.deltaY > 0 ? 0.9 : 1.1;
    this.engine.camera.zoomAt(screenPos.x, screenPos.y, factor, size.width, size.height);
    this.engine.markDirty();
  };

  private snapToGrid(p: Vec2): Vec2 {
    if (!this.engine.gridEnabled) return p;
    const s = this.engine.gridSpacing;
    return new Vec2(Math.round(p.x / s) * s, Math.round(p.y / s) * s);
  }

  private placeComponent(pos: Vec2): void {
    pos = this.snapToGrid(pos);
    let comp: Component;
    switch (this.toolMode) {
      case 'laser': comp = new LaserSource(pos, 0); break;
      case 'mirror': comp = new FlatMirror(pos, Math.PI / 4); break;
      case 'lens': comp = new Lens(pos, 0); break;
      case 'prism': comp = new Prism(pos, 0); break;
      case 'glass': comp = new GlassPlate(pos, 0); break;
      case 'curved_mirror': comp = new CurvedMirror(pos, 0); break;
      case 'aperture': comp = new Aperture(pos, 0); break;
      case 'beam_splitter': comp = new BeamSplitter(pos, Math.PI / 4); break;
      case 'detector': comp = new Detector(pos, 0); break;
      case 'diffraction_grating': comp = new DiffractionGrating(pos, 0); break;
      case 'medium': comp = new Medium(pos, 0); break;
      case 'polarizer': comp = new Polarizer(pos, 0); break;
      default: return;
    }

    this.engine.pushHistory();
    this.engine.scene.add(comp);
    this.engine.selectedId = comp.id;
    this.engine.markDirty();
    this.onComponentSelected?.(comp);
    this.onSceneChanged?.();

    this.setTool('select');
    document.querySelectorAll('.tool-btn').forEach(btn => btn.classList.remove('active'));
    document.querySelector('[data-tool="select"]')?.classList.add('active');
  }

  getMousePosition(): Vec2 {
    return this.mousePos;
  }

  isInPlacementMode(): boolean {
    return this.isPlacing;
  }
}
